import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchLanguages } from "../../Redux/Reducers/LanguageReducer";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import PageHeader1 from "../../components/common/PageHeader1";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { usePermissions } from "../../context/AuthContext";

function LanguageList() {
  const { can } = usePermissions();
  const dispatch = useDispatch();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const languageData = useSelector((state) => state?.LanguageReducer?.languages);
  const pagination = useSelector((state) => state?.LanguageReducer?.pagination);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterExcludeHiddenColumns: true,
    quickFilterValues: [''],
  });

  useEffect(() => {
    dispatch(fetchLanguages());
  }, [dispatch]);

  useEffect(() => {
    const { page, pageSize } = paginationModel;
    dispatch(fetchLanguages({ page: page + 1, limit: pageSize }));
  }, [paginationModel, dispatch]);

  const handleClickOpen = (userId) => {
    setSelectedUserId(userId);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUserId(null);
  };

  const handleDelete = async () => {
    setLoading(true);
    const token = localStorage.getItem("vedhvidhi-admin-token");

    try {
      await axios.delete(`${process.env.REACT_APP_BASEURL}/admin/language/${selectedUserId}`, {
        headers: {
          Authorization: `${token}`,
        },
      });

      toast.success("Language deleted successfully!");

      const totalDocs = pagination.totalDocs - 1;
      const totalPages = Math.ceil(totalDocs / paginationModel.pageSize);
      const newPage = Math.max(0, Math.min(paginationModel.page, totalPages - 1));

      setPaginationModel((prev) => ({
        ...prev,
        page: newPage,
      }));

      dispatch(fetchLanguages({ page: newPage + 1, limit: paginationModel.pageSize }));
    } catch (error) {
      toast.error("Failed to delete language. Please try again.");
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  const canEdit = can('languages:edit');
  const canDelete = can('languages:delete');

  const columns = [
    { field: "sono", headerName: "S.NO", width: 200 },
    { field: "name", headerName: "Language Name", width: 200 },
    { field: "createdAt", headerName: "Created At", width: 200 },
    { field: "updatedAt", headerName: "Updated At", width: 200 },
    ...(canEdit || canDelete
      ? [
        {
          field: "actions",
          headerName: "Actions",
          width: 150,
          sortable: false,
          filterable: false,
          renderCell: ({ row }) => (
            <Box display="flex" alignItems="center" gap={0.5}>
              {canEdit && (
                <Tooltip title="Edit Language">
                  <IconButton component={Link} to={`/language-edit/${row?.id}`}>
                    <i className="icofont-edit text-info"></i>
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Delete Language">
                  <IconButton onClick={() => handleClickOpen(row?.id)}>
                    <i className="icofont-ui-delete text-danger"></i>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ),
        },
      ]
      : []),
  ];


  const rows = languageData?.map((language, index) => ({
    sono: paginationModel.page * paginationModel.pageSize + index + 1,
    id: language._id,
    name: language.languageName,
    createdAt: language?.createdAt
      ? new Date(language?.createdAt).toLocaleString()
      : "",
    updatedAt: language?.updatedAt
      ? new Date(language?.updatedAt).toLocaleString()
      : "",
  })) || [];


  return (
    <div className="body d-flex py-1">
      <Toaster />
      <div className="container-xxl">
        <PageHeader1
          righttitle="Add Language"
          link="/language-add"
          routebutton={can('languages:add')}
          pagetitle='Language List'
        />
        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <div id="myDataTable_wrapper" className="dataTables_wrapper dt-bootstrap5 no-footer">
                  <div className="row">
                    <div className="col-sm-12">
                      <Box sx={{ width: 1 }}>
                        <Box sx={{ height: 400 }}>
                          <DataGrid
                            columns={columns}
                            rows={rows}
                            disableColumnFilter
                            disableDensitySelector
                            slots={{ toolbar: GridToolbar }}
                            filterModel={filterModel}
                            onFilterModelChange={(newModel) => setFilterModel(newModel)}
                            slotProps={{ toolbar: { showQuickFilter: true } }}
                            columnVisibilityModel={columnVisibilityModel}
                            onColumnVisibilityModelChange={(newModel) =>
                              setColumnVisibilityModel(newModel)
                            }
                            disableRowSelectionOnClick
                            paginationMode="server"
                            rowCount={pagination?.totalDocs || 0}
                            pageSizeOptions={[10, 25, 50, 100]}
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                          />
                        </Box>
                      </Box>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onClose={handleClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{"Delete Confirmation"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this language?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            autoFocus
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default LanguageList;